import { Fragment, useEffect, useMemo } from 'react';
import { useEffectOnce } from 'react-use';

import carIcon from '~/assets/icons/car.svg';
import garageIcon from '~/assets/icons/garage.svg';
import jobIcon from '~/assets/icons/job_3.svg';
import starIcon from '~/assets/icons/star.svg';
import worldIcon from '~/assets/icons/world.svg';
import { rage, useObservable } from '~/rage';

import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { Text } from '~/components/ui/Text';
import { DespawnAllButton } from '../../components/DespawnAllButton';
import { Divider } from '../../components/Divider';
import { MenuTabs } from '../../components/MenuTabs';
import { PanelFooter, PanelHeader } from '../../components/Panel';
import { PanelControls } from '../../components/PanelControls';
import { PanelTitle } from '../../components/PanelTitle';
import { ParkingSlotsInfo } from '../../components/ParkingSlotsInfo';
import { Section } from '../../components/Section';
import { VehicleCountStats } from '../../components/VehicleCountStats';
import { VehicleList } from '../../components/VehicleList';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import { syncVehicleState } from '../../redux/slices/vehicleListSlice';
import { resetStore } from '../../redux/wrappers';
import {
	StyledVehiclesContainer,
	StyledVehiclesList,
} from './PlayerVehicles.styled';

const PlayerVehicles = () => {
	const dispatch = useLayoutDispatch();
	const tabIndex = useLayoutSelector((state) => state.vehicleTabs.tabIndex);
	const selectedId = useLayoutSelector((state) => state.vehicleList.selectedId);
	const selectedList = useLayoutSelector(
		(state) => state.vehicleList.selectedList,
	);
	const spawnedVehicles = useLayoutSelector(
		(state) => state.vehicleList.spawnedVehicles,
	);
	const nonSpawnedVehicles = useLayoutSelector(
		(state) => state.vehicleList.nonSpawnedVehicles,
	);
	const workVehicles = useLayoutSelector(
		(state) => state.vehicleList.workVehicles,
	);
	const ownedVehicleList = useObservable(rage.vehicles.getPlayerVehicleList());
	const workVehicleList = useObservable(
		rage.vehicles.getPlayerWorkVehicleList(),
	);

	const scrollHeight = useMemo(() => {
		const SECTION_HEADER = 80;
		const ROW_HEIGHT = 60;
		const SECTION_GAP = 40;
		const BASE_HEIGHT = 560;

		let consumed = 0;
		if (workVehicles.length > 0) {
			consumed += SECTION_HEADER + ROW_HEIGHT * workVehicles.length;
		}
		if (spawnedVehicles.length > 0) {
			if (workVehicles.length > 0) consumed += SECTION_GAP;
			consumed += SECTION_HEADER + ROW_HEIGHT * spawnedVehicles.length;
		}

		return BASE_HEIGHT - consumed;
	}, [workVehicles.length, spawnedVehicles.length]);

	useEffect(() => {
		dispatch(
			syncVehicleState({
				vehicles: ownedVehicleList,
				workVehicles: workVehicleList,
				tabIndex,
			}),
		);
	}, [ownedVehicleList, workVehicleList, tabIndex]);

	useEffectOnce(() => {
		const handleDragging = (e: DragEvent) => {
			e.preventDefault();
		};
		document.addEventListener('dragstart', handleDragging);
		return () => document.removeEventListener('dragstart', handleDragging);
	});

	useEffectOnce(() => {
		return () => {
			dispatch(resetStore());
		};
	});

	useEffect(() => {
		const vehicleDiv = document.getElementById(`vehicle${selectedId}`);
		if (vehicleDiv !== null) {
			setTimeout(() => {
				vehicleDiv.scrollIntoView({ block: 'nearest' });
			}, 50);
		}
	}, [selectedId, selectedList]);

	const hasVisibleVehicles =
		workVehicles.length > 0 ||
		spawnedVehicles.length > 0 ||
		nonSpawnedVehicles.length > 0;

	return (
		<Fragment>
			<PanelHeader>
				<PanelTitle />
				<MenuTabs />
				<Section icon={garageIcon} text={'Мой гараж'}>
					<ParkingSlotsInfo />
					<VehicleCountStats />
				</Section>
				<Divider />
			</PanelHeader>
			<StyledVehiclesContainer>
				{workVehicles.length > 0 && (
					<Section
						icon={jobIcon}
						text={'Рабочий ТС'}
						nameStyle={{ paddingLeft: 60 }}
						style={{ width: 460 }}
						nameOpacity={1}
						color="#FABE2C"
					>
						<StyledVehiclesList>
							<VehicleList entries={workVehicles} list={'work'} />
						</StyledVehiclesList>
					</Section>
				)}
				{spawnedVehicles.length > 0 && (
					<Section
						icon={worldIcon}
						text={'Заспавненные ТС'}
						nameStyle={{ paddingLeft: 60 }}
						style={{
							marginTop: workVehicles.length > 0 ? 40 : 0,
							width: 460,
						}}
					>
						<DespawnAllButton />
						<StyledVehiclesList>
							<VehicleList entries={spawnedVehicles} list={'spawned'} />
						</StyledVehiclesList>
					</Section>
				)}
				{nonSpawnedVehicles.length > 0 && (
					<Section
						icon={tabIndex === 1 ? starIcon : carIcon}
						text={tabIndex === 1 ? 'Избранные ТС' : 'Все ТС'}
						style={{
							marginTop:
								workVehicles.length > 0 || spawnedVehicles.length > 0 ? 40 : 0,
							width: 460,
						}}
						nameStyle={{ paddingLeft: 60 }}
					>
						<ScrollContainer
							height={scrollHeight}
							scrollbarRight={20}
							width={460}
						>
							<StyledVehiclesList>
								<VehicleList entries={nonSpawnedVehicles} list={'nonSpawned'} />
							</StyledVehiclesList>
						</ScrollContainer>
					</Section>
				)}
				{!hasVisibleVehicles && (
					<div>
						<Section
							icon={tabIndex === 1 ? starIcon : carIcon}
							text={tabIndex === 1 ? 'Избранные ТС' : 'Все ТС'}
							style={{ width: 460 }}
							nameStyle={{ paddingLeft: 60 }}
						></Section>
						<Text
							style={{
								marginTop: 274,
								marginLeft: 60,
								fontSize: 18,
								opacity: 0.5,
								color: '#fff',
								textAlign: 'center',
							}}
						>
							{`К сожалению, но у вас нет\n ${
								tabIndex === 1 ? 'избранных ' : ''
							}ТС`}
						</Text>
					</div>
				)}
			</StyledVehiclesContainer>
			<PanelFooter>
				<PanelControls />
			</PanelFooter>
		</Fragment>
	);
};

export { PlayerVehicles };
